import { useState } from "react"
import PropTypes from "prop-types"
import { CopyToClipboard } from "react-copy-to-clipboard"
import DateTimePicker from "components/admin/events/DateTimePicker"
import { SavedBadge } from "components/shared/saved_badge"
import { Pane, RadioButton, Checkbox } from "components/ui"
import { Button } from "@planningcenter/tapestry-react"
import { apiAsync } from "utils/api"
import Icon from "components/planning_center_icon"
import moment from "moment-timezone"
import { colors } from "components/shared/admin_colors"
import pcoUrl from "pco-url"

let saveTimeout = null

const EventVisibility = ({
  event: eventFromProps,
  highlight: _highlight,
  mobileBrowser,
  ccaRegistrationsPublished,
  ccwRegistrationsPublished,
  isOrgAdmin
}) => {
  const [event, setEvent] = useState(eventFromProps)
  const [featured, setFeatured] = useState(event.featured)
  const [isScheduledToHide, setIsScheduledToHide] = useState(event.scheduledToHide)
  const [hideAt, setHideAt] = useState(event.hideAt)
  const [showAt, setShowAt] = useState(event.showAt)
  const [isEventSaved, setIsEventSaved] = useState(false)
  const [errors, setErrors] = useState({})
  const [linkCopied, setLinkCopied] = useState(false)
  const [editingVisibleScheduled, setEditingVisibleScheduled] = useState(false)
  const [editingHiddenScheduled, setEditingHiddenScheduled] = useState(false)
  const [isLinkOnly, setIsLinkOnly] = useState(event.linkOnly)
  const [isListed, setIsListed] = useState(event.listed)
  const [isScheduledToShow, setIsScheduledToShow] = useState(event.scheduledToShow)

  const paneTitle = "Church Center availability"

  const updateEvent = async params => {
    setErrors([])
    setIsEventSaved(false)

    if (saveTimeout) {
      clearTimeout(saveTimeout)
    }

    const response = await apiAsync.put(event.paths.show, { event: params })

    if (response.errors) {
      setErrors(response.errors)
    } else {
      setEvent(response.event)
      setIsEventSaved(true)
      saveTimeout = setTimeout(() => setIsEventSaved(false), 5000)
    }
  }

  const handleVisibilityChange = ({ target: { value } }) => {
    const now = moment()

    if (value === "scheduled") {
      const eventStartTime = moment(event.startTime)
      const nextweek = now.clone().add(7, "days")
      const newShowAtDate =
        eventStartTime.isValid() && eventStartTime.diff(now) >= 0 ? eventStartTime : nextweek

      setEditingVisibleScheduled(true)
      setIsScheduledToShow(true)
      setShowAt(
        newShowAtDate
          .startOf("day")
          .add(8, "hours")
          .format()
      )
    }

    if (value === "linkOnly") {
      setIsLinkOnly(true)
      setIsListed(false)
      setIsScheduledToHide(false)
      setIsScheduledToShow(false)
      updateEvent({
        show_at: "",
        hide_at: ""
      })
    }

    if (value === "listed") {
      setIsLinkOnly(false)
      setIsListed(true)
      setIsScheduledToShow(false)
      let hideAt = event.hideAt

      if (moment(hideAt).isBefore(now)) {
        hideAt = null
        setIsScheduledToHide(false)
      }

      updateEvent({
        show_at: now.startOf("day").format(),
        hide_at: hideAt
      })
    }
  }

  const handleShowAtSave = () => {
    setIsScheduledToShow(true)
    setEditingVisibleScheduled(false)
    updateEvent({
      show_at: showAt
    })
  }

  const handleHideAtSave = () => {
    updateEvent({
      hide_at: hideAt
    })
    setEditingHiddenScheduled(false)
  }

  const handleHideAutomatically = ({ target: { checked } }) => {
    setIsScheduledToHide(checked)
    setEditingHiddenScheduled(true)

    if (checked === false) {
      setHideAt(null)
      updateEvent({ hide_at: null })
    } else {
      setHideAt(event.hideAt ?? event.endTime)
    }
  }

  const handleFeatureChange = ({ target: { checked } }) => {
    setFeatured(checked)
    updateEvent({ featured: checked })
  }

  return (
    <>
      {event.isArchived && (
        <div className="pane">
          <div className="pane__header d-f ai-c jc-sb">
            <h2 className="pane__title">{paneTitle}</h2>
            {isEventSaved ? <SavedBadge /> : null}
          </div>
          <div className="p-2 c-neutral-600">
            This signup is archived so it is not visible on Church Center.
          </div>
        </div>
      )}

      {!event.isArchived && (
        <Pane>
          <Pane.Header>
            <Pane.Title>{paneTitle}</Pane.Title>
            {isEventSaved ? <SavedBadge /> : null}
          </Pane.Header>
          {ccaRegistrationsPublished || ccwRegistrationsPublished ? (
            <>
              <Pane.Body>
                <label htmlFor="publicUrl">View this signup on Church Center at:</label>

                <div className="d-f">
                  <input
                    type="text"
                    value={event.publicUrl}
                    id="publicUrl"
                    className="f-1"
                    readOnly
                  />
                  <div className="ml-1 d-f ai-s">
                    <CopyToClipboard text={event.publicUrl} onCopy={() => setLinkCopied(true)}>
                      <Button title={linkCopied ? "Copied" : "Copy"} variant="outline" size="sm" />
                    </CopyToClipboard>

                    <a
                      className="btn-outline sm ml-1"
                      href={event.publicUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <span className="p-r t-2p">
                        <Icon symbol="general#new-window" />
                      </span>
                      View
                    </a>
                  </div>
                </div>
              </Pane.Body>
              <Pane.Body paddingTop={0}>
                <label htmlFor="visibility">Include on your church&apos;s signups page</label>

                <RadioButton
                  text="Now"
                  name="visibility"
                  htmlFor="visibilityPublic"
                  checked={isListed && !isScheduledToShow}
                  value="listed"
                  onSelect={handleVisibilityChange}
                />
                <RadioButton
                  text="On a specific date/time"
                  name="visibility"
                  htmlFor="visibilityScheduled"
                  checked={isScheduledToShow}
                  value="scheduled"
                  onSelect={handleVisibilityChange}
                />
                {errors.show_at && <div className="c-error pt-4p">Date {errors.show_at[0]}</div>}
                {isScheduledToShow && (
                  <div className="mb-1">
                    {editingVisibleScheduled ? (
                      <DateTimePicker
                        datetime={showAt}
                        onChange={setShowAt}
                        onButtonClick={handleShowAtSave}
                        isMobile={mobileBrowser}
                        errors={{
                          date: errors.show_at
                        }}
                      />
                    ) : (
                      <>
                        {showAt && (
                          <div className="d-f ai-c pl-3" css={{ color: colors.neutral_020 }}>
                            on {moment(showAt).format(momentDateFormat)} at{" "}
                            {moment(showAt).format(momentTimeFormat)}
                            <button
                              className="btn-outline xs ml-1"
                              onClick={() => setEditingVisibleScheduled(true)}
                            >
                              Edit
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
                <RadioButton
                  text="Do not include (direct link only)"
                  name="visibility"
                  htmlFor="visibilityPrivate"
                  checked={isLinkOnly && !isScheduledToShow}
                  value="linkOnly"
                  wrapperClasses=""
                  onSelect={handleVisibilityChange}
                />
              </Pane.Body>

              {(!isLinkOnly || isScheduledToShow) && (
                <Pane.Body paddingTop={0}>
                  <Checkbox
                    text="Automatically hide from signups page"
                    htmlFor="hideAutomatically"
                    value="hideAutomatically"
                    checked={isScheduledToHide}
                    onSelect={handleHideAutomatically}
                  />
                  {errors.hide_at && <div className="c-error pt-4p">Date {errors.hide_at[0]}</div>}
                  {isScheduledToHide && (
                    <div className="mb-1">
                      {editingHiddenScheduled ? (
                        <DateTimePicker
                          datetime={hideAt}
                          onChange={setHideAt}
                          onButtonClick={handleHideAtSave}
                          isMobile={mobileBrowser}
                          errors={{
                            date: errors.hide_at
                          }}
                        />
                      ) : (
                        <>
                          {hideAt && (
                            <div className="d-f ai-c pl-3 mb-1" css={{ color: colors.neutral_020 }}>
                              on {moment(hideAt).format(momentDateFormat)} at{" "}
                              {moment(hideAt).format(momentTimeFormat)}
                              <button
                                className="btn-outline xs ml-1"
                                onClick={() => setEditingHiddenScheduled(true)}
                              >
                                Edit
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                  <Checkbox
                    text="Featured"
                    htmlFor="featured"
                    value="featured"
                    checked={featured}
                    onSelect={handleFeatureChange}
                    helperMessage="Featured signups are shown at the top of the signups list on Church Center."
                  />
                </Pane.Body>
              )}
            </>
          ) : (
            <Pane.Body>
              <strong>Registrations is not published on Church Center.</strong> People cannot
              register or browse signups on the web or mobile app.{" "}
              {isOrgAdmin && (
                <>
                  <a href={`${pcoUrl(window.railsEnv)("accounts")}/organization?tab=church_center`}>
                    Update settings
                  </a>
                  .
                </>
              )}
            </Pane.Body>
          )}
        </Pane>
      )}
    </>
  )
}

EventVisibility.propTypes = {
  event: PropTypes.shape({
    visibility: PropTypes.bool,
    featured: PropTypes.bool,
    isArchived: PropTypes.bool,
    isSimple: PropTypes.bool,
    publicUrl: PropTypes.string,
    scheduledToHide: PropTypes.bool,
    hideAt: PropTypes.string,
    scheduledToShow: PropTypes.bool,
    showAt: PropTypes.string,
    listed: PropTypes.bool,
    linkOnly: PropTypes.bool,
    endTime: PropTypes.string,
    paths: PropTypes.shape({
      show: PropTypes.string
    }),
    startTime: PropTypes.string
  }),
  mobileBrowser: PropTypes.bool,
  highlight: PropTypes.bool,
  ccaRegistrationsPublished: PropTypes.bool,
  ccwRegistrationsPublished: PropTypes.bool,
  isOrgAdmin: PropTypes.bool
}

export default EventVisibility
